import {
  Box,
  Typography,
  Paper,
  Chip,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import { useEffect, useRef, useState } from "react";
import { Phrase, PhraseType, findPhrases } from "../../../api/phrasebook";
import { getGroupById } from "../../../utils/content-groups";
import { useNotification } from "@refinedev/core";
import usePhrasebookStore from "./store";
import PhrasesLegend from "./phrases-legend";
import EditorPhrasebookSearchItems from "./search-items";
import EditorPhrasebookSearchJobs from "./search-jobs";
import { BrandBatchSettings } from "../../../pages/playground/types";
import { useAuthStore } from "../../../authProvider";

export default function EditorPhrasebookSearch({
  originalLanguage,
  settings,
  brandsWithGlobalPhrases,
  updatePhrasesByType,
  defaultSelectedBrands,
}: {
  originalLanguage?: string;
  settings: BrandBatchSettings;
  brandsWithGlobalPhrases: { id: string; name: string }[];
  updatePhrasesByType: (
    type: PhraseType,
    phrases: Phrase[],
    append?: boolean
  ) => void;
  defaultSelectedBrands: string[];
}) {
  const { user } = useAuthStore();

  const { open } = useNotification();

  const {
    batchId,
    searchTab,
    query,
    activeField,
    setActiveField,
    garmentType,
    refreshSearch,
    setRefreshSearch,
    selectedBrandIds,
    setSelectedBrandIds,
    matchSuitableFor,
    setMatchSuitableFor,
    startKeywordSearch,
    // searchOptionTab = "exact",
    // setSearchOptionTab,
  } = usePhrasebookStore();

  const [loading, setLoading] = useState<boolean>(false);
  const [phrases, setPhrases] = useState<Phrase[]>([]);

  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const scrollToTopRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    return () => {
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
      }
    };
  }, []);

  if (!selectedBrandIds) {
    setSelectedBrandIds(defaultSelectedBrands);
  }

  useEffect(() => {
    // Search phrases with debounce
    const handleSearch = (value: string) => {
      setRefreshSearch(false);
      setPhrases([...phrases.slice(0, 20)]);

      // Clear any previous timeout
      if (searchTimeout.current) {
        clearTimeout(searchTimeout.current);
        searchTimeout.current = null;
      }

      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      abortControllerRef.current = new AbortController();
      const signal = abortControllerRef.current.signal;

      // Show loading indicator
      setLoading(true);

      // Debounce search with 300ms delay (increased from 200ms for better protection)
      searchTimeout.current = setTimeout(() => {
        // Determine suitability flags based on active field's content group kind
        const activeGroup = activeField?.type ? getGroupById(settings, activeField.type) : undefined;
        const suitableForTitle =
          matchSuitableFor && activeGroup?.kind === "title" ? true : undefined;
        const suitableForDescription =
          matchSuitableFor && activeGroup?.kind === "description" ? true : undefined;
        const suitableForBulletPoints =
          matchSuitableFor && activeGroup?.kind === "bullet_points" ? true : undefined;

        findPhrases({
          brandIds:
            selectedBrandIds?.map((id) => String(id)) || defaultSelectedBrands,
          batchId,
          page: 1,
          limit: 500,
          query: value.replace(/<[^>]+>/g, ""),
          suitableForTitle,
          suitableForDescription,
          suitableForBulletPoints,
          signal,
        })
          .then((response) => {
            if (!signal.aborted) {
              const loadedPhrases = response.data;
              setPhrases(loadedPhrases as unknown as Phrase[]);
            }
          })
          .catch((e) => {
            if (e?.name === "AbortError" || e?.name === "CanceledError") {
              return;
            }

            open?.({
              message: "Failed to fetch phrases.",
              type: "error",
            });

            setPhrases([]);
          })
          .finally(() => {
            if (abortControllerRef.current?.signal === signal) {
              setLoading(false);
              abortControllerRef.current = null;
              searchTimeout.current = null;

              // Scroll to top when search is complete
              if (scrollToTopRef.current) {
                scrollToTopRef.current.scrollTop = 0;
              }
            }
          });
      }, 600);
    };

    if (searchTab === "phrases") {
      handleSearch(query);
    }
  }, [
    searchTab,
    query,
    selectedBrandIds,
    brandsWithGlobalPhrases,
    batchId,
    open,
    garmentType,
    refreshSearch,
    activeField,
    matchSuitableFor,
  ]);

  return (
    <Paper
      elevation={0}
      sx={{
        display: "flex",
        flexDirection: "column",
        backgroundColor: "background.paper",
        height: "100%",
        width: "100%",
      }}
    >
      <Box sx={{ flex: 1, overflow: "auto" }} ref={scrollToTopRef}>
        {searchTab === "phrases" && (
          <>
            <Box
              sx={{
                p: 1.5,
                overflow: "auto",
                borderBottom: "1px solid #e0e0e0",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                position: "sticky",
                backgroundColor: "background.paper",
                top: 0,
                zIndex: 1,
                gap: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  flex: 1,
                }}
              >
                {/* Brands Section */}
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: 0.5,
                  }}
                >
                  {brandsWithGlobalPhrases &&
                    brandsWithGlobalPhrases.length > 0 &&
                    brandsWithGlobalPhrases.map((brand) => (
                      <Chip
                        key={brand.id}
                        label={brand.name}
                        size="small"
                        variant={
                          selectedBrandIds &&
                          selectedBrandIds.indexOf(brand.id) > -1
                            ? "filled"
                            : "outlined"
                        }
                        color={
                          selectedBrandIds &&
                          selectedBrandIds.indexOf(brand.id) > -1
                            ? "secondary"
                            : "default"
                        }
                        onClick={() => {
                          const newSelectedBrandIds = selectedBrandIds
                            ? [...selectedBrandIds]
                            : [];

                          const isBrandSelected =
                            newSelectedBrandIds.indexOf(brand.id) > -1;

                          if (isBrandSelected) {
                            setSelectedBrandIds(
                              newSelectedBrandIds.filter(
                                (id) => id !== brand.id
                              )
                            );
                          } else {
                            setSelectedBrandIds([
                              ...newSelectedBrandIds,
                              brand.id,
                            ]);
                          }
                        }}
                        sx={{
                          fontSize: "0.75rem",
                          height: "24px",
                          cursor: "pointer",
                        }}
                      />
                    ))}
                </Box>

                {/* Match Suitable For Toggle */}
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={matchSuitableFor}
                      onChange={(event) =>
                        setMatchSuitableFor(event.target.checked)
                      }
                    />
                  }
                  label={
                    <Typography variant="caption" sx={{ fontSize: "0.75rem" }}>
                      Match Suitable For
                    </Typography>
                  }
                  sx={{ margin: 0 }}
                />
              </Box>

              <Clear
                sx={{
                  fontSize: "1.5rem",
                  color: "black",
                  cursor: "pointer",
                  justifyContent: "flex-end",
                }}
                onClick={() => {
                  setActiveField(null);
                }}
              />
            </Box>

            <EditorPhrasebookSearchItems
              phrases={phrases}
              loading={loading}
              query={query}
              brandsWithGlobalPhrases={brandsWithGlobalPhrases}
              languagesToTranslate={settings.languages_to_translate || []}
              onUsageCountClick={(phrase) => {
                startKeywordSearch(phrase);
              }}
              originalLanguage={originalLanguage}
            />
          </>
        )}

        {searchTab === "jobs" && (
          <EditorPhrasebookSearchJobs
            onCopyPhrases={updatePhrasesByType}
            batchSettings={settings}
          />
        )}
      </Box>

      {!user?.isClientRelated && searchTab === "phrases" && <PhrasesLegend />}
    </Paper>
  );
}
